import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiBriefcase,
    FiGlobe,
    FiMail,
    FiMapPin,
    FiMonitor,
    FiSave,
    FiShoppingBag,
    FiSmartphone,
    FiUser,
} from "react-icons/fi";

import {
    BusinessFormField,
} from "./components";


/*
|--------------------------------------------------------------------------
| DEFAULT BUSINESS
|--------------------------------------------------------------------------
*/

const DEFAULT_BUSINESS = {
    name:
        "",

    legalName:
        "",

    code:
        "",

    subdomain:
        "",

    mobile:
        "",

    email:
        "",

    gstNumber:
        "",

    panNumber:
        "",

    customDomain:
        "",

    subscriptionStatus:
        "TRIAL",

    trialEndsAt:
        "",

    storefrontEnabled:
        false,

    marketplaceEnabled:
        false,

    address: {
        line1:
            "",

        line2:
            "",

        city:
            "",

        state:
            "",

        pincode:
            "",

        country:
            "India",
    },

    branding: {
        logo:
            "",

        favicon:
            "",

        primaryColor:
            "",

        secondaryColor:
            "",
    },
};


/*
|--------------------------------------------------------------------------
| DEFAULT OWNER
|--------------------------------------------------------------------------
*/

const DEFAULT_OWNER = {
    name:
        "",

    mobile:
        "",

    email:
        "",

    password:
        "",
};


/*
|--------------------------------------------------------------------------
| NORMALIZE MOBILE
|--------------------------------------------------------------------------
*/

const normalizeMobile = (
    value
) => {

    return String(
        value ||
        ""
    )
        .replace(
            /\D/g,
            ""
        )
        .slice(
            0,
            10
        );

};


/*
|--------------------------------------------------------------------------
| NORMALIZE BUSINESS CODE
|--------------------------------------------------------------------------
*/

const normalizeBusinessCode = (
    value
) => {

    return String(
        value ||
        ""
    )
        .toUpperCase()
        .replace(
            /[^A-Z0-9_]/g,
            "_"
        )
        .replace(
            /_+/g,
            "_"
        )
        .replace(
            /^_+|_+$/g,
            ""
        )
        .slice(
            0,
            30
        );

};


/*
|--------------------------------------------------------------------------
| NORMALIZE SUBDOMAIN
|--------------------------------------------------------------------------
*/

const normalizeSubdomain = (
    value
) => {

    return String(
        value ||
        ""
    )
        .toLowerCase()
        .replace(
            /[^a-z0-9-]/g,
            "-"
        )
        .replace(
            /-+/g,
            "-"
        )
        .replace(
            /^-+|-+$/g,
            ""
        )
        .slice(
            0,
            50
        );

};


/*
|--------------------------------------------------------------------------
| DATE FOR INPUT
|--------------------------------------------------------------------------
*/

const getDateInputValue = (
    value
) => {

    if (
        !value
    ) {

        return "";

    }


    const date =
        new Date(
            value
        );


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return "";

    }


    return date
        .toISOString()
        .slice(
            0,
            10
        );

};


/*
|--------------------------------------------------------------------------
| BUILD INITIAL BUSINESS
|--------------------------------------------------------------------------
*/

const buildInitialBusiness = (
    initialData
) => {

    if (
        !initialData
    ) {

        return {
            ...DEFAULT_BUSINESS,

            address: {
                ...DEFAULT_BUSINESS.address,
            },

            branding: {
                ...DEFAULT_BUSINESS.branding,
            },
        };

    }


    return {
        ...DEFAULT_BUSINESS,

        ...initialData,

        mobile:
            normalizeMobile(
                initialData?.mobile
            ),

        trialEndsAt:
            getDateInputValue(
                initialData?.trialEndsAt
            ),

        storefrontEnabled:
            Boolean(
                initialData
                    ?.storefrontEnabled
            ),

        marketplaceEnabled:
            Boolean(
                initialData
                    ?.marketplaceEnabled
            ),

        address: {
            ...DEFAULT_BUSINESS.address,

            ...(
                initialData?.address ||
                {}
            ),
        },

        branding: {
            ...DEFAULT_BUSINESS.branding,

            ...(
                initialData?.branding ||
                {}
            ),
        },
    };

};


/*
|--------------------------------------------------------------------------
| PLATFORM BUSINESS FORM
|--------------------------------------------------------------------------
|
| CREATE:
|
| <PlatformBusinessForm
|   mode="create"
|   onSubmit={handleCreate}
| />
|
|
| EDIT:
|
| <PlatformBusinessForm
|   mode="edit"
|   initialData={business}
|   onSubmit={handleUpdate}
| />
|--------------------------------------------------------------------------
*/

const PlatformBusinessForm = ({
    mode =
        "create",

    initialData =
        null,

    loading =
        false,

    onSubmit,

    onCancel,

    submitLabel,
}) => {

    /*
    |--------------------------------------------------------------------------
    | MODE
    |--------------------------------------------------------------------------
    */

    const isCreate =
        mode ===
        "create";


    const isEdit =
        mode ===
        "edit";


    /*
    |--------------------------------------------------------------------------
    | BUSINESS STATE
    |--------------------------------------------------------------------------
    */

    const [
        business,
        setBusiness,
    ] =
        useState(
            () =>
                buildInitialBusiness(
                    initialData
                )
        );


    /*
    |--------------------------------------------------------------------------
    | OWNER STATE
    |--------------------------------------------------------------------------
    */

    const [
        owner,
        setOwner,
    ] =
        useState({
            ...DEFAULT_OWNER,
        });


    /*
    |--------------------------------------------------------------------------
    | ERRORS
    |--------------------------------------------------------------------------
    */

    const [
        errors,
        setErrors,
    ] =
        useState(
            {}
        );


    /*
    |--------------------------------------------------------------------------
    | RESET WHEN INITIAL DATA CHANGES
    |--------------------------------------------------------------------------
    |
    | Important for details page:
    |
    | request finishes
    |       ↓
    | initialData changes
    |       ↓
    | form receives latest business
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {

            setBusiness(
                buildInitialBusiness(
                    initialData
                )
            );


            if (
                isCreate
            ) {

                setOwner({
                    ...DEFAULT_OWNER,
                });

            }


            setErrors(
                {}
            );

        },
        [
            initialData,
            isCreate,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | TITLE
    |--------------------------------------------------------------------------
    */

    const finalSubmitLabel =
        useMemo(
            () => {

                if (
                    submitLabel
                ) {

                    return submitLabel;

                }


                return isCreate
                    ? "Create Business"
                    : "Save Changes";

            },
            [
                submitLabel,
                isCreate,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | CLEAR ERROR
    |--------------------------------------------------------------------------
    */

    const clearError = (
        key
    ) => {

        setErrors(
            (
                previous
            ) => ({
                ...previous,

                [key]:
                    "",

                form:
                    "",
            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | BUSINESS CHANGE
    |--------------------------------------------------------------------------
    */

    const handleBusinessChange =
        (
            event
        ) => {

            const {
                name,
                value,
                type,
                checked,
            } =
                event.target;


            let nextValue =
                type ===
                "checkbox"
                    ? checked
                    : value;


            /*
            |--------------------------------------------------------------------------
            | Mobile
            |--------------------------------------------------------------------------
            */

            if (
                name ===
                "mobile"
            ) {

                nextValue =
                    normalizeMobile(
                        value
                    );

            }


            /*
            |--------------------------------------------------------------------------
            | Code
            |--------------------------------------------------------------------------
            */

            if (
                name ===
                "code"
            ) {

                nextValue =
                    normalizeBusinessCode(
                        value
                    );

            }


            /*
            |--------------------------------------------------------------------------
            | Subdomain
            |--------------------------------------------------------------------------
            */

            if (
                name ===
                "subdomain"
            ) {

                nextValue =
                    normalizeSubdomain(
                        value
                    );

            }


            /*
            |--------------------------------------------------------------------------
            | Custom Domain
            |--------------------------------------------------------------------------
            */

            if (
                name ===
                "customDomain"
            ) {

                nextValue =
                    String(
                        value ||
                        ""
                    )
                        .trimStart()
                        .toLowerCase();

            }


            setBusiness(
                (
                    previous
                ) => ({
                    ...previous,

                    [name]:
                        nextValue,
                })
            );


            clearError(
                `business.${name}`
            );

        };


    /*
    |--------------------------------------------------------------------------
    | ADDRESS CHANGE
    |--------------------------------------------------------------------------
    */

    const handleAddressChange =
        (
            event
        ) => {

            const {
                name,
                value,
            } =
                event.target;


            let nextValue =
                value;


            if (
                name ===
                "pincode"
            ) {

                nextValue =
                    value
                        .replace(
                            /\D/g,
                            ""
                        )
                        .slice(
                            0,
                            6
                        );

            }


            setBusiness(
                (
                    previous
                ) => ({
                    ...previous,

                    address: {
                        ...previous.address,

                        [name]:
                            nextValue,
                    },
                })
            );

        };


    /*
    |--------------------------------------------------------------------------
    | BRANDING CHANGE
    |--------------------------------------------------------------------------
    */

    const handleBrandingChange =
        (
            event
        ) => {

            const {
                name,
                value,
            } =
                event.target;


            setBusiness(
                (
                    previous
                ) => ({
                    ...previous,

                    branding: {
                        ...previous.branding,

                        [name]:
                            value,
                    },
                })
            );

        };


    /*
    |--------------------------------------------------------------------------
    | OWNER CHANGE
    |--------------------------------------------------------------------------
    */

    const handleOwnerChange =
        (
            event
        ) => {

            const {
                name,
                value,
            } =
                event.target;


            let nextValue =
                value;


            if (
                name ===
                "mobile"
            ) {

                nextValue =
                    normalizeMobile(
                        value
                    );

            }


            setOwner(
                (
                    previous
                ) => ({
                    ...previous,

                    [name]:
                        nextValue,
                })
            );


            clearError(
                `owner.${name}`
            );

        };


    /*
    |--------------------------------------------------------------------------
    | VALIDATE
    |--------------------------------------------------------------------------
    */

    const validate =
        () => {

            const nextErrors =
                {};


            /*
            |--------------------------------------------------------------------------
            | Business Name
            |--------------------------------------------------------------------------
            */

            if (
                !String(
                    business?.name ||
                    ""
                ).trim()
            ) {

                nextErrors[
                    "business.name"
                ] =
                    "Business name is required.";

            }


            /*
            |--------------------------------------------------------------------------
            | Business Mobile
            |--------------------------------------------------------------------------
            */

            if (
                business?.mobile &&
                !/^[6-9]\d{9}$/.test(
                    business.mobile
                )
            ) {

                nextErrors[
                    "business.mobile"
                ] =
                    "Enter a valid 10-digit mobile number.";

            }


            /*
            |--------------------------------------------------------------------------
            | Business Email
            |--------------------------------------------------------------------------
            */

            if (
                business?.email &&
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                    business.email
                )
            ) {

                nextErrors[
                    "business.email"
                ] =
                    "Enter a valid business email.";

            }


            /*
            |--------------------------------------------------------------------------
            | Create Mode Owner Validation
            |--------------------------------------------------------------------------
            */

            if (
                isCreate
            ) {

                if (
                    !String(
                        owner?.name ||
                        ""
                    ).trim()
                ) {

                    nextErrors[
                        "owner.name"
                    ] =
                        "Owner name is required.";

                }


                if (
                    !/^[6-9]\d{9}$/.test(
                        owner?.mobile ||
                        ""
                    )
                ) {

                    nextErrors[
                        "owner.mobile"
                    ] =
                        "Enter a valid 10-digit owner mobile number.";

                }


                if (
                    owner?.email &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                        owner.email
                    )
                ) {

                    nextErrors[
                        "owner.email"
                    ] =
                        "Enter a valid owner email.";

                }


                if (
                    String(
                        owner?.password ||
                        ""
                    ).length <
                    8
                ) {

                    nextErrors[
                        "owner.password"
                    ] =
                        "Owner password must contain at least 8 characters.";

                }

            }


            setErrors(
                nextErrors
            );


            return (
                Object.keys(
                    nextErrors
                ).length ===
                0
            );

        };


    /*
    |--------------------------------------------------------------------------
    | BUILD CREATE PAYLOAD
    |--------------------------------------------------------------------------
    */

    const buildCreatePayload =
        () => {

            return {
                business: {
                    name:
                        String(
                            business.name ||
                            ""
                        ).trim(),

                    legalName:
                        String(
                            business.legalName ||
                            ""
                        ).trim(),

                    code:
                        String(
                            business.code ||
                            ""
                        ).trim(),

                    subdomain:
                        String(
                            business.subdomain ||
                            ""
                        ).trim(),

                    mobile:
                        normalizeMobile(
                            business.mobile
                        ),

                    email:
                        String(
                            business.email ||
                            ""
                        )
                            .trim()
                            .toLowerCase(),

                    gstNumber:
                        String(
                            business.gstNumber ||
                            ""
                        )
                            .trim()
                            .toUpperCase(),

                    panNumber:
                        String(
                            business.panNumber ||
                            ""
                        )
                            .trim()
                            .toUpperCase(),

                    customDomain:
                        String(
                            business.customDomain ||
                            ""
                        )
                            .trim()
                            .toLowerCase(),

                    subscriptionStatus:
                        business
                            .subscriptionStatus ||
                        "TRIAL",

                    trialEndsAt:
                        business
                            .trialEndsAt ||
                        null,

                    storefrontEnabled:
                        Boolean(
                            business
                                .storefrontEnabled
                        ),

                    marketplaceEnabled:
                        Boolean(
                            business
                                .marketplaceEnabled
                        ),

                    address: {
                        ...business.address,
                    },

                    branding: {
                        ...business.branding,
                    },
                },


                owner: {
                    name:
                        String(
                            owner.name ||
                            ""
                        ).trim(),

                    mobile:
                        normalizeMobile(
                            owner.mobile
                        ),

                    email:
                        String(
                            owner.email ||
                            ""
                        )
                            .trim()
                            .toLowerCase(),

                    password:
                        owner.password,
                },
            };

        };


    /*
    |--------------------------------------------------------------------------
    | BUILD EDIT PAYLOAD
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | code
    | subdomain
    | owner
    |
    | are intentionally excluded.
    |--------------------------------------------------------------------------
    */

    const buildEditPayload =
        () => {

            return {
                name:
                    String(
                        business.name ||
                        ""
                    ).trim(),

                legalName:
                    String(
                        business.legalName ||
                        ""
                    ).trim(),

                mobile:
                    normalizeMobile(
                        business.mobile
                    ),

                email:
                    String(
                        business.email ||
                        ""
                    )
                        .trim()
                        .toLowerCase(),

                address: {
                    ...business.address,
                },

                gstNumber:
                    String(
                        business.gstNumber ||
                        ""
                    )
                        .trim()
                        .toUpperCase(),

                panNumber:
                    String(
                        business.panNumber ||
                        ""
                    )
                        .trim()
                        .toUpperCase(),

                customDomain:
                    String(
                        business.customDomain ||
                        ""
                    )
                        .trim()
                        .toLowerCase(),

                storefrontEnabled:
                    Boolean(
                        business
                            .storefrontEnabled
                    ),

                marketplaceEnabled:
                    Boolean(
                        business
                            .marketplaceEnabled
                    ),

                subscriptionStatus:
                    business
                        .subscriptionStatus,

                trialEndsAt:
                    business
                        .trialEndsAt ||
                    null,

                branding: {
                    ...business.branding,
                },
            };

        };


    /*
    |--------------------------------------------------------------------------
    | SUBMIT
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (
            event
        ) => {

            event.preventDefault();


            if (
                loading
            ) {

                return;

            }


            if (
                !validate()
            ) {

                return;

            }


            const payload =
                isCreate
                    ? buildCreatePayload()
                    : buildEditPayload();


            if (
                typeof onSubmit ===
                "function"
            ) {

                await onSubmit(
                    payload
                );

            }

        };


    /*
    |--------------------------------------------------------------------------
    | FORM
    |--------------------------------------------------------------------------
    */

    return (

        <form
            className="platform-business-form"
            onSubmit={
                handleSubmit
            }
            noValidate
        >

            {/*
            |--------------------------------------------------------------------------
            | FORM ERROR
            |--------------------------------------------------------------------------
            */}

            {errors.form ? (

                <div className="platform-business-form-error">

                    {
                        errors.form
                    }

                </div>

            ) : null}


            {/*
            |--------------------------------------------------------------------------
            | BUSINESS INFORMATION
            |--------------------------------------------------------------------------
            */}

            <section className="platform-business-form-section">

                <div className="platform-business-form-section-title">

                    <FiBriefcase />


                    <div>

                        <strong>

                            Business Information

                        </strong>


                        <span>

                            Basic tenant and business information.

                        </span>

                    </div>

                </div>


                <div className="platform-business-form-grid">

                    <BusinessFormField
                        label="Business Name"
                        required
                        error={
                            errors[
                                "business.name"
                            ]
                        }
                    >

                        <input
                            type="text"
                            name="name"
                            value={
                                business.name
                            }
                            onChange={
                                handleBusinessChange
                            }
                            placeholder="Business name"
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="Legal Name"
                    >

                        <input
                            type="text"
                            name="legalName"
                            value={
                                business.legalName
                            }
                            onChange={
                                handleBusinessChange
                            }
                            placeholder="Registered legal name"
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    {/*
                    |--------------------------------------------------------------------------
                    | CREATE-ONLY IDENTITY
                    |--------------------------------------------------------------------------
                    */}

                    {isCreate ? (

                        <>

                            <BusinessFormField
                                label="Business Code"
                            >

                                <input
                                    type="text"
                                    name="code"
                                    value={
                                        business.code
                                    }
                                    onChange={
                                        handleBusinessChange
                                    }
                                    placeholder="Auto-generated if empty"
                                    disabled={
                                        loading
                                    }
                                />

                            </BusinessFormField>


                            <BusinessFormField
                                label="Subdomain"
                            >

                                <div className="platform-business-input-icon">

                                    <FiGlobe />


                                    <input
                                        type="text"
                                        name="subdomain"
                                        value={
                                            business.subdomain
                                        }
                                        onChange={
                                            handleBusinessChange
                                        }
                                        placeholder="Auto-generated if empty"
                                        disabled={
                                            loading
                                        }
                                    />

                                </div>

                            </BusinessFormField>

                        </>

                    ) : (

                        <>

                            <BusinessFormField
                                label="Business Code"
                            >

                                <input
                                    type="text"
                                    value={
                                        business.code ||
                                        "-"
                                    }
                                    disabled
                                />

                            </BusinessFormField>


                            <BusinessFormField
                                label="Subdomain"
                            >

                                <input
                                    type="text"
                                    value={
                                        business.subdomain ||
                                        "-"
                                    }
                                    disabled
                                />

                            </BusinessFormField>

                        </>

                    )}


                    <BusinessFormField
                        label="Business Mobile"
                        error={
                            errors[
                                "business.mobile"
                            ]
                        }
                    >

                        <div className="platform-business-input-icon">

                            <FiSmartphone />


                            <input
                                type="tel"
                                name="mobile"
                                value={
                                    business.mobile
                                }
                                onChange={
                                    handleBusinessChange
                                }
                                placeholder="10-digit mobile number"
                                disabled={
                                    loading
                                }
                            />

                        </div>

                    </BusinessFormField>


                    <BusinessFormField
                        label="Business Email"
                        error={
                            errors[
                                "business.email"
                            ]
                        }
                    >

                        <div className="platform-business-input-icon">

                            <FiMail />


                            <input
                                type="email"
                                name="email"
                                value={
                                    business.email
                                }
                                onChange={
                                    handleBusinessChange
                                }
                                placeholder="business@example.com"
                                disabled={
                                    loading
                                }
                            />

                        </div>

                    </BusinessFormField>


                    <BusinessFormField
                        label="GST Number"
                    >

                        <input
                            type="text"
                            name="gstNumber"
                            value={
                                business.gstNumber
                            }
                            onChange={
                                handleBusinessChange
                            }
                            placeholder="GSTIN"
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="PAN Number"
                    >

                        <input
                            type="text"
                            name="panNumber"
                            value={
                                business.panNumber
                            }
                            onChange={
                                handleBusinessChange
                            }
                            placeholder="PAN number"
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="Custom Domain"
                        full
                    >

                        <div className="platform-business-input-icon">

                            <FiGlobe />


                            <input
                                type="text"
                                name="customDomain"
                                value={
                                    business.customDomain
                                }
                                onChange={
                                    handleBusinessChange
                                }
                                placeholder="store.example.com"
                                disabled={
                                    loading
                                }
                            />

                        </div>

                    </BusinessFormField>

                </div>

            </section>


            {/*
            |--------------------------------------------------------------------------
            | ADDRESS
            |--------------------------------------------------------------------------
            */}

            <section className="platform-business-form-section">

                <div className="platform-business-form-section-title">

                    <FiMapPin />


                    <div>

                        <strong>

                            Business Address

                        </strong>


                        <span>

                            Registered business location.

                        </span>

                    </div>

                </div>


                <div className="platform-business-form-grid">

                    <BusinessFormField
                        label="Address Line 1"
                        full
                    >

                        <input
                            type="text"
                            name="line1"
                            value={
                                business.address
                                    .line1
                            }
                            onChange={
                                handleAddressChange
                            }
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="Address Line 2"
                        full
                    >

                        <input
                            type="text"
                            name="line2"
                            value={
                                business.address
                                    .line2
                            }
                            onChange={
                                handleAddressChange
                            }
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="City"
                    >

                        <input
                            type="text"
                            name="city"
                            value={
                                business.address
                                    .city
                            }
                            onChange={
                                handleAddressChange
                            }
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="State"
                    >

                        <input
                            type="text"
                            name="state"
                            value={
                                business.address
                                    .state
                            }
                            onChange={
                                handleAddressChange
                            }
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="Pincode"
                    >

                        <input
                            type="text"
                            name="pincode"
                            value={
                                business.address
                                    .pincode
                            }
                            onChange={
                                handleAddressChange
                            }
                            inputMode="numeric"
                            maxLength={
                                6
                            }
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="Country"
                    >

                        <input
                            type="text"
                            name="country"
                            value={
                                business.address
                                    .country
                            }
                            onChange={
                                handleAddressChange
                            }
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>

                </div>

            </section>


            {/*
            |--------------------------------------------------------------------------
            | SUBSCRIPTION
            |--------------------------------------------------------------------------
            */}

            <section className="platform-business-form-section">

                <div className="platform-business-form-section-title">

                    <FiShoppingBag />


                    <div>

                        <strong>

                            Subscription & Features

                        </strong>


                        <span>

                            Control tenant access and enabled products.

                        </span>

                    </div>

                </div>


                <div className="platform-business-form-grid">

                    <BusinessFormField
                        label="Subscription Status"
                    >

                        <select
                            name="subscriptionStatus"
                            value={
                                business.subscriptionStatus
                            }
                            onChange={
                                handleBusinessChange
                            }
                            disabled={
                                loading
                            }
                        >

                            <option value="TRIAL">

                                Trial

                            </option>


                            <option value="ACTIVE">

                                Active

                            </option>


                            <option value="EXPIRED">

                                Expired

                            </option>


                            <option value="SUSPENDED">

                                Suspended

                            </option>

                        </select>

                    </BusinessFormField>


                    <BusinessFormField
                        label="Trial Ends At"
                    >

                        <input
                            type="date"
                            name="trialEndsAt"
                            value={
                                business.trialEndsAt
                            }
                            onChange={
                                handleBusinessChange
                            }
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <div className="platform-business-checkbox-field">

                        <label>

                            <input
                                type="checkbox"
                                name="storefrontEnabled"
                                checked={
                                    business
                                        .storefrontEnabled
                                }
                                onChange={
                                    handleBusinessChange
                                }
                                disabled={
                                    loading
                                }
                            />


                            <span>

                                <FiMonitor />

                                Storefront Enabled

                            </span>

                        </label>

                    </div>


                    <div className="platform-business-checkbox-field">

                        <label>

                            <input
                                type="checkbox"
                                name="marketplaceEnabled"
                                checked={
                                    business
                                        .marketplaceEnabled
                                }
                                onChange={
                                    handleBusinessChange
                                }
                                disabled={
                                    loading
                                }
                            />


                            <span>

                                <FiShoppingBag />

                                Marketplace Enabled

                            </span>

                        </label>

                    </div>

                </div>

            </section>


            {/*
            |--------------------------------------------------------------------------
            | BRANDING
            |--------------------------------------------------------------------------
            */}

            <section className="platform-business-form-section">

                <div className="platform-business-form-section-title">

                    <FiMonitor />


                    <div>

                        <strong>

                            Branding

                        </strong>


                        <span>

                            Tenant-specific visual configuration.

                        </span>

                    </div>

                </div>


                <div className="platform-business-form-grid">

                    <BusinessFormField
                        label="Logo URL"
                    >

                        <input
                            type="text"
                            name="logo"
                            value={
                                business.branding
                                    .logo
                            }
                            onChange={
                                handleBrandingChange
                            }
                            placeholder="Logo URL"
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="Favicon URL"
                    >

                        <input
                            type="text"
                            name="favicon"
                            value={
                                business.branding
                                    .favicon
                            }
                            onChange={
                                handleBrandingChange
                            }
                            placeholder="Favicon URL"
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="Primary Color"
                    >

                        <input
                            type="text"
                            name="primaryColor"
                            value={
                                business.branding
                                    .primaryColor
                            }
                            onChange={
                                handleBrandingChange
                            }
                            placeholder="#2563EB"
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>


                    <BusinessFormField
                        label="Secondary Color"
                    >

                        <input
                            type="text"
                            name="secondaryColor"
                            value={
                                business.branding
                                    .secondaryColor
                            }
                            onChange={
                                handleBrandingChange
                            }
                            placeholder="#111827"
                            disabled={
                                loading
                            }
                        />

                    </BusinessFormField>

                </div>

            </section>


            {/*
            |--------------------------------------------------------------------------
            | OWNER
            |--------------------------------------------------------------------------
            |
            | Only Create Business needs owner information.
            |--------------------------------------------------------------------------
            */}

            {isCreate ? (

                <section className="platform-business-form-section">

                    <div className="platform-business-form-section-title">

                        <FiUser />


                        <div>

                            <strong>

                                Business Owner

                            </strong>


                            <span>

                                This account receives the tenant OWNER role.

                            </span>

                        </div>

                    </div>


                    <div className="platform-business-form-grid">

                        <BusinessFormField
                            label="Owner Name"
                            required
                            error={
                                errors[
                                    "owner.name"
                                ]
                            }
                        >

                            <input
                                type="text"
                                name="name"
                                value={
                                    owner.name
                                }
                                onChange={
                                    handleOwnerChange
                                }
                                placeholder="Owner name"
                                disabled={
                                    loading
                                }
                            />

                        </BusinessFormField>


                        <BusinessFormField
                            label="Owner Mobile"
                            required
                            error={
                                errors[
                                    "owner.mobile"
                                ]
                            }
                        >

                            <div className="platform-business-input-icon">

                                <FiSmartphone />


                                <input
                                    type="tel"
                                    name="mobile"
                                    value={
                                        owner.mobile
                                    }
                                    onChange={
                                        handleOwnerChange
                                    }
                                    placeholder="10-digit mobile"
                                    inputMode="numeric"
                                    maxLength={
                                        10
                                    }
                                    disabled={
                                        loading
                                    }
                                />

                            </div>

                        </BusinessFormField>


                        <BusinessFormField
                            label="Owner Email"
                            error={
                                errors[
                                    "owner.email"
                                ]
                            }
                        >

                            <div className="platform-business-input-icon">

                                <FiMail />


                                <input
                                    type="email"
                                    name="email"
                                    value={
                                        owner.email
                                    }
                                    onChange={
                                        handleOwnerChange
                                    }
                                    placeholder="owner@example.com"
                                    disabled={
                                        loading
                                    }
                                />

                            </div>

                        </BusinessFormField>


                        <BusinessFormField
                            label="Owner Password"
                            required
                            error={
                                errors[
                                    "owner.password"
                                ]
                            }
                        >

                            <input
                                type="password"
                                name="password"
                                value={
                                    owner.password
                                }
                                onChange={
                                    handleOwnerChange
                                }
                                placeholder="Minimum 8 characters"
                                autoComplete="new-password"
                                disabled={
                                    loading
                                }
                            />

                        </BusinessFormField>

                    </div>

                </section>

            ) : null}


            {/*
            |--------------------------------------------------------------------------
            | ACTIONS
            |--------------------------------------------------------------------------
            */}

            <div className="platform-business-form-actions">

                {typeof onCancel ===
                "function" ? (

                    <button
                        type="button"
                        className="platform-business-cancel-button"
                        onClick={
                            onCancel
                        }
                        disabled={
                            loading
                        }
                    >

                        Cancel

                    </button>

                ) : null}


                <button
                    type="submit"
                    className="platform-business-save-button"
                    disabled={
                        loading
                    }
                >

                    {loading ? (

                        <>

                            <span className="platform-business-button-loader" />

                            {isCreate
                                ? "Creating..."
                                : "Saving..."}

                        </>

                    ) : (

                        <>

                            <FiSave />

                            {
                                finalSubmitLabel
                            }

                        </>

                    )}

                </button>

            </div>

        </form>

    );

};


export default PlatformBusinessForm;